import { ArrowLeft, Shield, Database, Users } from "lucide-react"
import { Link } from "react-router"

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/login"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Privacy Policy</h1>
            <p className="text-sm text-gray-500">Last updated: January 27, 2026</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          {/* Quick Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 rounded-lg p-4">
              <Shield className="h-6 w-6 text-blue-600 mb-2" />
              <h3 className="font-medium text-gray-900">Your Data is Protected</h3>
              <p className="text-sm text-gray-600">Encrypted at rest and in transit</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <Users className="h-6 w-6 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900">Limited Sharing</h3>
              <p className="text-sm text-gray-600">Only with essential service providers</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <Database className="h-6 w-6 text-purple-600 mb-2" />
              <h3 className="font-medium text-gray-900">You Control Your Data</h3>
              <p className="text-sm text-gray-600">Export or delete anytime</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="prose prose-gray max-w-none">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Audra Flow. This Privacy Policy explains how we collect, use, disclose,
              and safeguard your information when you use our product management and design platform.
            </p>

            <h2>2. Information We Collect</h2>
            <h3>Personal Information</h3>
            <ul>
              <li><strong>Email Address</strong> - For account identification and notifications</li>
              <li><strong>Full Name</strong> - For display in the application</li>
              <li><strong>Avatar/Profile Image</strong> - For personalization</li>
              <li><strong>Azure AD Identifier</strong> - For Single Sign-On authentication</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li><strong>IP Address</strong> - For security and rate limiting</li>
              <li><strong>User Agent</strong> - For debugging and security</li>
              <li><strong>Actions Performed</strong> - For audit logging</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <ul>
              <li>Provide and maintain the Service</li>
              <li>Authenticate users and manage accounts</li>
              <li>Enable collaboration features</li>
              <li>Process and analyze uploaded documents</li>
              <li>Generate AI-powered insights and artifacts</li>
              <li>Maintain security through audit logging</li>
            </ul>

            <h2>4. Third-Party Data Sharing</h2>
            <p>We share data with the following providers to deliver our service:</p>
            <ul>
              <li><strong>OpenAI</strong> - AI-powered features (document analysis, chat, generation)</li>
              <li><strong>Microsoft Azure AD</strong> - Single Sign-On authentication</li>
              <li><strong>Amazon Web Services</strong> - Cloud infrastructure hosting (including PostgreSQL with pgvector for vector search)</li>
            </ul>

            <h2>5. Data Security</h2>
            <p>We implement appropriate security measures including:</p>
            <ul>
              <li>Encryption at rest (AES-256)</li>
              <li>Encryption in transit (TLS 1.2+)</li>
              <li>Role-based access control (RBAC)</li>
              <li>bcrypt password hashing</li>
              <li>Comprehensive audit logging</li>
            </ul>

            <h2>6. Data Retention</h2>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left">Data Type</th>
                  <th className="text-left">Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>User Account Data</td>
                  <td>Until account deletion</td>
                </tr>
                <tr>
                  <td>Project Data</td>
                  <td>Until project deletion + 30 days</td>
                </tr>
                <tr>
                  <td>Audit Logs</td>
                  <td>7 years</td>
                </tr>
                <tr>
                  <td>AI Conversation History</td>
                  <td>1 year after last activity</td>
                </tr>
              </tbody>
            </table>

            <h2>7. Your Rights</h2>
            <ul>
              <li><strong>Access</strong> - Request a copy of your personal data</li>
              <li><strong>Correction</strong> - Request correction of inaccurate data</li>
              <li><strong>Deletion</strong> - Request deletion of your data</li>
              <li><strong>Data Portability</strong> - Export your data in a machine-readable format</li>
              <li><strong>Withdraw Consent</strong> - Withdraw consent at any time</li>
            </ul>

            <h2>8. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul>
              <li>Email: <a href="mailto:privacy@audraflow.io">privacy@audraflow.io</a></li>
              <li>Data Protection Officer: <a href="mailto:dpo@audraflow.io">dpo@audraflow.io</a></li>
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div>
                <span>Version 1.0</span>
                <span className="mx-2">•</span>
                <span>Effective: January 27, 2026</span>
              </div>
              <Link
                to="/terms"
                className="text-blue-600 hover:text-blue-700"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
